import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Combobox, Transition } from '@headlessui/react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';
import EditInstitutionProfileModal from '../components/EditInstitutionProfileModal';
import ManageSubAdminsModal from '../components/ManageSubAdminsModal';
import demoData from '../data/demo_institutions.json';
import {
  PencilIcon,
  PlusIcon,
  UserGroupIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  UsersIcon,
  EllipsisHorizontalIcon,
  ChevronRightIcon,
  KeyIcon,
  XMarkIcon,
  TrashIcon,
  ChevronUpDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface UserSearchResult {
  id: string;
  email: string;
  full_name: string | null;
}

interface InstitutionDetail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  cover_image_url: string | null;
  is_active: boolean;
  group_count: number | null;
  member_count: number | null;
}

interface GroupSummary {
  id: string;
  institution_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  student_count: number | null;
  book_count: number | null;
}

interface AdminInfo {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
}

const InstitutionHome: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [institution, setInstitution] = useState<InstitutionDetail | null>(null);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  // Both institution_admin and institution_sub_admin members, listed
  // inline on the page so admins are visible at a glance instead of
  // hidden behind the "Manage Admins" / "Sub-Admins" modals.
  const [staff, setStaff] = useState<AdminInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showSubAdmins, setShowSubAdmins] = useState(false);
  const [showManageAdmins, setShowManageAdmins] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const isDemo = id?.startsWith('demo-');

  const myRole = user?.institution_memberships?.find(
    m => m.institution_id === id && ['institution_admin', 'institution_sub_admin'].includes(m.role)
  )?.role;
  const isAdmin = user?.is_admin || myRole === 'institution_admin';
  const isAdminOrSub = user?.is_admin || myRole === 'institution_admin' || myRole === 'institution_sub_admin';
  const isPlatformAdmin = !!user?.is_admin;

  const fetchData = useCallback(async () => {
    // Demo institutions — load from JSON, no API calls
    if (isDemo) {
      const demoInst = demoData.institutions.find((i) => i.id === id);
      if (demoInst) {
        setInstitution({
          id: demoInst.id,
          name: demoInst.name,
          slug: demoInst.slug,
          description: demoInst.description,
          logo_url: demoInst.logo_url,
          cover_image_url: demoInst.cover_image_url,
          is_active: demoInst.is_active,
          group_count: demoInst.group_count,
          member_count: demoInst.member_count,
        });
        setGroups(demoInst.groups.map((g) => ({
          id: g.id,
          institution_id: demoInst.id,
          name: g.name,
          description: g.description,
          is_active: g.is_active,
          student_count: g.student_count,
          book_count: g.book_count,
        })));
        // Demo institutes don't carry staff data — leave empty.
        setStaff([]);
      }
      setLoading(false);
      return;
    }

    try {
      const [instRes, groupsRes, membersRes] = await Promise.all([
        api.get(`/api/institutions/${id}`),
        api.get(`/api/institutions/${id}/groups`),
        api.get(`/api/institutions/${id}/members`),
      ]);
      setInstitution(instRes.data);
      setGroups(groupsRes.data);
      setStaff(
        (membersRes.data as AdminInfo[]).filter(
          m => m.role === 'institution_admin' || m.role === 'institution_sub_admin'
        )
      );
    } catch (error) {
      console.error('Failed to fetch institution data:', error);
    } finally {
      setLoading(false);
    }
  }, [id, isDemo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Inline staff removal — same endpoints the modals use, so backend
  // guarantees (last-admin protection, role gates, etc.) apply here too.
  // Errors surface via alert() since these are operator actions on a
  // management page; mock + sub-admin both go through their respective
  // role-typed endpoint per the existing API.
  const handleRemoveStaff = useCallback(async (member: AdminInfo) => {
    const isInstAdmin = member.role === 'institution_admin';
    const label = isInstAdmin ? 'institution admin' : 'sub-admin';
    const who = member.full_name || member.email;
    if (!window.confirm(`Remove ${who} as ${label}? Their account and any books they uploaded stay intact.`)) return;
    try {
      const path = isInstAdmin ? 'institution-admins' : 'admins';
      await api.delete(`/api/institutions/${id}/${path}/${member.user_id}`);
      fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.detail || `Failed to remove ${label}`);
    }
  }, [id, fetchData]);

  useEffect(() => {
    if (!menuOpenId) return;
    const close = () => setMenuOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpenId]);

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreating(true);
    try {
      await api.post(`/api/institutions/${id}/groups`, {
        name: newGroupName.trim(),
        description: newGroupDesc.trim() || null,
      });
      setNewGroupName('');
      setNewGroupDesc('');
      setShowCreateGroup(false);
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleGroup = async (groupId: string, currentActive: boolean) => {
    try {
      await api.put(`/api/institutions/${id}/groups/${groupId}`, { is_active: !currentActive });
      setMenuOpenId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle group:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
        </div>
      </Layout>
    );
  }

  if (!institution) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Institution not found.</p>
        </div>
      </Layout>
    );
  }

  const btnOutline = `px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all ${
    isDarkMode
      ? 'bg-slate-800/80 hover:bg-slate-700 border border-slate-700/80 text-gray-300'
      : 'bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700'
  }`;

  return (
    <Layout>
      <div className="min-h-full">

        {/* ─── Cover Banner ─── */}
        <div className="h-44 sm:h-52 md:h-56 relative overflow-hidden">
          {institution.cover_image_url ? (
            <img src={institution.cover_image_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700" />
          )}
          <div className={`absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t ${
            isDarkMode ? 'from-[#030712] to-transparent' : 'from-gray-50 to-transparent'
          }`} />
        </div>

        {/* ─── Profile Content ─── */}
        <div className="px-4 sm:px-6 md:px-8 max-w-5xl mx-auto">

          {/* Logo */}
          <div className="relative -mt-14 sm:-mt-16">
            {institution.logo_url ? (
              <img
                src={institution.logo_url}
                alt={institution.name}
                className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shadow-xl ${
                  isDarkMode ? 'ring-4 ring-[#030712]' : 'ring-4 ring-gray-50'
                }`}
              />
            ) : (
              <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center shadow-xl ${
                isDarkMode ? 'ring-4 ring-[#030712] bg-slate-800' : 'ring-4 ring-gray-50 bg-gray-100'
              }`}>
                <span className="text-2xl sm:text-3xl font-bold text-cyan-500">{institution.name[0]}</span>
              </div>
            )}
          </div>

          {/* Name + Description */}
          <div className="mt-5">
            <h1 className={`text-xl sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {institution.name}
            </h1>
            {institution.description && (
              <p className={`mt-1.5 text-sm sm:text-base leading-relaxed max-w-2xl ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {institution.description}
              </p>
            )}
          </div>

          {/* Stats */}
          <div className={`flex items-center gap-5 mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="flex items-center gap-1.5">
              <UserGroupIcon className={`h-4 w-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {institution.group_count ?? 0}
              </span>
              <span className="text-sm">groups</span>
            </div>
            <div className="flex items-center gap-1.5">
              <UsersIcon className={`h-4 w-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {institution.member_count ?? 0}
              </span>
              <span className="text-sm">members</span>
            </div>
          </div>

          {/* Action Buttons */}
          {(isAdminOrSub || isPlatformAdmin) && (
            <div className="flex flex-wrap gap-2 mt-5">
              {isAdminOrSub && (
                <button onClick={() => setShowEditProfile(true)} className={btnOutline}>
                  <PencilIcon className="h-4 w-4" /> Edit Profile
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setShowSubAdmins(true)} className={btnOutline}>
                  <ShieldCheckIcon className="h-4 w-4" /> Sub-Admins
                </button>
              )}
              {isPlatformAdmin && (
                <button onClick={() => setShowManageAdmins(true)} className={btnOutline}>
                  <KeyIcon className="h-4 w-4" /> Manage Admins
                </button>
              )}
            </div>
          )}

          {/* Divider */}
          <div className={`border-t mt-6 mb-6 sm:mt-8 sm:mb-8 ${
            isDarkMode ? 'border-slate-800' : 'border-gray-200'
          }`} />

          {/* ─── Admins & Sub-Admins ─── */}
          <div className="pb-8">
            <div className="flex items-center justify-between mb-2">
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Admins & Sub-Admins
              </h2>
              {/* Quick entry to the existing modals — same controls as the
                  action buttons in the hero, repeated here so an operator
                  who's already looking at the staff list can act in place. */}
              {(isPlatformAdmin || isAdmin) && (
                <div className="flex gap-2">
                  {isAdmin && (
                    <button onClick={() => setShowSubAdmins(true)} className={btnOutline}>
                      <ShieldCheckIcon className="h-4 w-4" /> Sub-Admins
                    </button>
                  )}
                  {isPlatformAdmin && (
                    <button onClick={() => setShowManageAdmins(true)} className={btnOutline}>
                      <KeyIcon className="h-4 w-4" /> Manage Admins
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Membership model hint — students/teachers live inside groups,
                so this section is intentionally just the institute-wide
                staff. Without this note, an operator might wonder where the
                "Add User" button is at this level. */}
            <p className={`text-sm mb-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Sub-admins manage the institute alongside the admin. Teachers and students live inside <span className="font-medium">Groups</span> (below) and are added per group.
            </p>

            {staff.length === 0 ? (
              <div className={`text-center py-10 sm:py-12 rounded-2xl border-2 border-dashed ${
                isDarkMode ? 'border-slate-700/60' : 'border-gray-200'
              }`}>
                <ShieldCheckIcon className={`h-10 w-10 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={`font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>No admins yet</p>
                <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Use the buttons above to add an institution admin or sub-admin.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {staff.map((member) => {
                  const isInstAdmin = member.role === 'institution_admin';
                  // Inline-delete visibility mirrors the backend role gates:
                  //   institution_admin row → only platform admins (matches
                  //     get_current_admin_user on DELETE /institution-admins/...)
                  //   sub-admin row → institute admin OR platform admin
                  //     (matches require_institution_admin on DELETE /admins/...)
                  // This keeps the trash icon from appearing for users who'd
                  // get a 403 if they clicked it.
                  const canRemove = isInstAdmin
                    ? isPlatformAdmin
                    : (isAdmin || isPlatformAdmin);
                  return (
                    <div
                      key={member.id}
                      className={`group/staff flex items-center p-3 sm:p-4 rounded-xl border ${
                        isDarkMode
                          ? 'bg-slate-800/30 border-slate-800/60'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className={`hidden sm:flex flex-shrink-0 w-10 h-10 rounded-xl items-center justify-center mr-3 ${
                        isInstAdmin
                          ? (isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50')
                          : (isDarkMode ? 'bg-violet-500/10' : 'bg-violet-50')
                      }`}>
                        {isInstAdmin
                          ? <KeyIcon className={`h-5 w-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                          : <ShieldCheckIcon className={`h-5 w-5 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                        }
                      </div>

                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {member.full_name || member.email}
                          </h3>
                          <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                            isInstAdmin
                              ? (isDarkMode ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-50 text-cyan-700')
                              : (isDarkMode ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-50 text-violet-700')
                          }`}>
                            {isInstAdmin ? 'Admin' : 'Sub-Admin'}
                          </span>
                        </div>
                        {member.full_name && (
                          <p className={`text-sm mt-0.5 truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {member.email}
                          </p>
                        )}
                      </div>

                      {canRemove && (
                        <button
                          onClick={() => handleRemoveStaff(member)}
                          className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                            isDarkMode
                              ? 'text-red-400 hover:bg-red-500/10 hover:text-red-300'
                              : 'text-red-500 hover:bg-red-50 hover:text-red-600'
                          }`}
                          title={`Remove ${isInstAdmin ? 'admin' : 'sub-admin'}`}
                          aria-label={`Remove ${member.full_name || member.email} as ${isInstAdmin ? 'admin' : 'sub-admin'}`}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className={`border-t mb-6 sm:mb-8 ${
            isDarkMode ? 'border-slate-800' : 'border-gray-200'
          }`} />

          {/* ─── Groups ─── */}
          <div className="pb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Groups</h2>
              {isAdminOrSub && (
                <button
                  onClick={() => setShowCreateGroup(true)}
                  className="px-4 py-2 rounded-xl text-sm font-medium text-white flex items-center gap-1.5 transition-all hover:shadow-lg hover:shadow-cyan-500/20"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  <PlusIcon className="h-4 w-4" /> Create Group
                </button>
              )}
            </div>

            {groups.length === 0 ? (
              <div className={`text-center py-14 sm:py-16 rounded-2xl border-2 border-dashed ${
                isDarkMode ? 'border-slate-700/60' : 'border-gray-200'
              }`}>
                <UserGroupIcon className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={`font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>No groups yet</p>
                <p className={`text-sm mb-6 max-w-sm mx-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Groups represent classes or batches. Create one, then add teachers and students to it from the group page.
                </p>
                {isAdminOrSub && (
                  <button
                    onClick={() => setShowCreateGroup(true)}
                    className="px-5 py-2.5 rounded-xl text-sm font-medium text-white inline-flex items-center gap-1.5 hover:shadow-lg hover:shadow-cyan-500/20 transition-all"
                    style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                  >
                    <PlusIcon className="h-4 w-4" /> Create Group
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {groups.map((group) => (
                  <div
                    key={group.id}
                    className={`group/card flex items-center p-3 sm:p-4 rounded-xl border cursor-pointer transition-all ${
                      isDarkMode
                        ? 'bg-slate-800/30 border-slate-800/60 hover:bg-slate-800/60 hover:border-slate-700'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                    } ${!group.is_active ? 'opacity-40' : ''}`}
                    onClick={() => navigate(`/institution/${id}/groups/${group.id}`)}
                  >
                    {/* Icon */}
                    <div className={`hidden sm:flex flex-shrink-0 w-10 h-10 rounded-xl items-center justify-center mr-3 ${
                      isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50'
                    }`}>
                      <UserGroupIcon className={`h-5 w-5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 mr-3">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {group.name}
                        </h3>
                        {!group.is_active && (
                          <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 font-medium">
                            Inactive
                          </span>
                        )}
                      </div>
                      {group.description && (
                        <p className={`text-sm mt-0.5 truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {group.description}
                        </p>
                      )}
                    </div>

                    {/* Stats + menu */}
                    <div className={`flex items-center gap-3 sm:gap-5 flex-shrink-0 text-sm ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <div className="flex items-center gap-1.5" title="Members">
                        <UsersIcon className="h-4 w-4" />
                        <span>{group.student_count ?? 0}</span>
                      </div>
                      <div className="hidden sm:flex items-center gap-1.5" title="Books">
                        <BookOpenIcon className="h-4 w-4" />
                        <span>{group.book_count ?? 0}</span>
                      </div>

                      {isAdminOrSub && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenId(menuOpenId === group.id ? null : group.id);
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                            }`}
                          >
                            <EllipsisHorizontalIcon className="h-5 w-5" />
                          </button>
                          {menuOpenId === group.id && (
                            <div className={`absolute right-0 top-9 z-30 w-44 rounded-xl shadow-xl border py-1 ${
                              isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-lg'
                            }`}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleGroup(group.id, group.is_active);
                                }}
                                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                                  group.is_active
                                    ? 'text-red-400 hover:bg-red-500/10'
                                    : 'text-green-400 hover:bg-green-500/10'
                                }`}
                              >
                                {group.is_active ? 'Deactivate Group' : 'Activate Group'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      <ChevronRightIcon className={`h-4 w-4 transition-transform group-hover/card:translate-x-0.5 ${
                        isDarkMode ? 'text-gray-600' : 'text-gray-300'
                      }`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Modals ─── */}

      {showCreateGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl ${
            isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'
          }`}>
            <div className="p-6">
              <h3 className={`text-lg font-semibold mb-5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Create Group
              </h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Group name (e.g. JEE 2027 Batch A)"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  autoFocus
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none transition-colors ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-500 focus:border-cyan-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  }`}
                />
                <textarea
                  placeholder="Description (optional)"
                  value={newGroupDesc}
                  onChange={(e) => setNewGroupDesc(e.target.value)}
                  rows={2}
                  className={`w-full px-4 py-2.5 rounded-xl border text-sm outline-none resize-none transition-colors ${
                    isDarkMode
                      ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-500 focus:border-cyan-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
                  }`}
                />
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => { setShowCreateGroup(false); setNewGroupName(''); setNewGroupDesc(''); }}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${
                    isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  disabled={creating || !newGroupName.trim()}
                  className="px-5 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditProfile && (
        <EditInstitutionProfileModal
          institution={institution}
          onClose={() => setShowEditProfile(false)}
          onSaved={fetchData}
        />
      )}

      {showSubAdmins && (
        <ManageSubAdminsModal
          institutionId={institution.id}
          onClose={() => setShowSubAdmins(false)}
          onChanged={fetchData}
        />
      )}

      {showManageAdmins && (
        <ManageAdminsModal
          institutionId={institution.id}
          onClose={() => setShowManageAdmins(false)}
          onChanged={fetchData}
        />
      )}
    </Layout>
  );
};

// ─── Manage Institution Admins Modal (platform admin only) ───

interface ManageAdminsModalProps {
  institutionId: string;
  onClose: () => void;
  onChanged?: () => void;
}

const ManageAdminsModal: React.FC<ManageAdminsModalProps> = ({ institutionId, onClose, onChanged }) => {
  const { isDarkMode } = useTheme();
  const [admins, setAdmins] = useState<AdminInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  // Typeahead state — replaces the previous plain email input. Operator types
  // a fragment, we hit /users/search debounced, they pick a row from the
  // dropdown. Avoids the "did I spell their email right?" failure mode.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<UserSearchResult | null>(null);
  const debounceRef = useRef<number | null>(null);

  const fetchAdmins = useCallback(async () => {
    try {
      const response = await api.get(`/api/institutions/${institutionId}/members`);
      setAdmins(response.data.filter((m: AdminInfo) => m.role === 'institution_admin'));
    } catch (err) {
      console.error('Failed to fetch admins:', err);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  // Debounced server-side user search. 250ms feels fast enough that typing
  // doesn't lag, slow enough that we don't spam the endpoint per keystroke.
  // Min 2 chars matches the backend Query() validation.
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const response = await api.get(
          `/api/institutions/${institutionId}/users/search`,
          { params: { q: trimmed, limit: 10 } }
        );
        setResults(response.data);
      } catch (err) {
        console.error('User search failed:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, institutionId]);

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    setError('');
    try {
      await api.post(`/api/institutions/${institutionId}/institution-admins`, { email: selected.email });
      setSelected(null);
      setQuery('');
      setResults([]);
      fetchAdmins();
      onChanged?.();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add admin');
    } finally {
      setAdding(false);
    }
  };

  // Existing-admin email set so we can flag them in the dropdown — picking
  // an existing admin would just 4xx from the backend, better to dim it.
  const existingAdminEmails = new Set(admins.map(a => a.email.toLowerCase()));

  const handleRemove = async (userId: string) => {
    if (!window.confirm('Remove this institution admin?')) return;
    try {
      await api.delete(`/api/institutions/${institutionId}/institution-admins/${userId}`);
      fetchAdmins();
      onChanged?.();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const inputClass = `flex-1 px-3 py-2 rounded-xl border text-sm outline-none transition-colors ${
    isDarkMode
      ? 'bg-slate-900/50 border-slate-600 text-white placeholder-gray-500 focus:border-cyan-500'
      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-cyan-500'
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl ${
        isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white'
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <KeyIcon className="h-5 w-5 text-cyan-500" />
              Institution Admins
            </h3>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-slate-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <Combobox
              value={selected}
              onChange={(value: UserSearchResult | null) => setSelected(value)}
            >
              <div className="relative flex-1">
                <Combobox.Input
                  className={inputClass}
                  displayValue={(u: UserSearchResult | null) => u?.email ?? ''}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by email or name (min 2 chars)"
                  autoComplete="off"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                </Combobox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => {
                    // Clear results only — keep `query` so the user can still
                    // see what they typed; reset only after a successful add.
                    setResults([]);
                  }}
                >
                  <Combobox.Options
                    className={`absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-xl py-1 text-sm shadow-lg ring-1 ring-black/5 ${
                      isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'
                    }`}
                  >
                    {searching ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Searching…
                      </div>
                    ) : query.trim().length < 2 ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Type at least 2 characters
                      </div>
                    ) : results.length === 0 ? (
                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        No users match &ldquo;{query}&rdquo;
                      </div>
                    ) : (
                      results.map((u) => {
                        const isExisting = existingAdminEmails.has(u.email.toLowerCase());
                        return (
                          <Combobox.Option
                            key={u.id}
                            value={u}
                            disabled={isExisting}
                            className={({ active, disabled }) =>
                              `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                disabled
                                  ? 'opacity-50 cursor-not-allowed'
                                  : active
                                    ? 'bg-cyan-500 text-white'
                                    : isDarkMode ? 'text-slate-200' : 'text-gray-900'
                              }`
                            }
                          >
                            {({ selected: optSelected }) => (
                              <>
                                <div className="truncate font-medium">
                                  {u.full_name || u.email}
                                </div>
                                {u.full_name && (
                                  <div className="truncate text-xs opacity-75">{u.email}</div>
                                )}
                                {isExisting && (
                                  <div className="truncate text-xs opacity-75 italic">
                                    Already an admin
                                  </div>
                                )}
                                {optSelected && !isExisting && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                                    <CheckIcon className="h-4 w-4" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        );
                      })
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
            <button
              onClick={handleAdd}
              disabled={adding || !selected}
              className="px-4 py-2 rounded-xl text-white text-sm font-medium disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)' }}
            >
              {adding ? '...' : 'Add'}
            </button>
          </div>
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500" />
            </div>
          ) : admins.length === 0 ? (
            <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              No institution admins assigned yet.
            </p>
          ) : (
            <div className="space-y-2">
              {admins.map((admin) => (
                <div key={admin.id} className={`flex items-center justify-between p-3 rounded-xl ${
                  isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'
                }`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {admin.full_name || admin.email}
                    </div>
                    {admin.full_name && (
                      <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {admin.email}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemove(admin.user_id)}
                    className="flex-shrink-0 text-red-400 hover:text-red-300 p-1.5 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end mt-5">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                isDarkMode ? 'text-gray-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstitutionHome;
